import type { ComponentType, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { CalendarRange, Snowflake, Timer, type LucideIcon } from 'lucide-react';

/**
 * As abas do calendário de mudanças e seus endereços.
 *
 * A lista mora aqui, e não dentro da tela, porque o roteador também precisa
 * dela: cada aba tem que ter rota registrada, e uma aba sem rota é 404 na cara
 * de quem clicou. Mesmo motivo de `features/admin/navegacao.ts`.
 *
 * A conversão de tipo do `Link` fica concentrada neste arquivo pela razão de
 * sempre: o `to` do TanStack Router é tipado contra a árvore registrada em
 * `app/router.tsx`, e este módulo é compilado antes de a rota existir lá.
 */

export const RAIZ_DE_MUDANCAS = '/mudancas';

export const CAMINHOS = {
  calendario: 'calendario',
  janelas: 'janelas',
  congelamentos: 'congelamentos',
} as const;

export type CaminhoDeMudanca = (typeof CAMINHOS)[keyof typeof CAMINHOS];

export interface AbaDeMudanca {
  caminho: CaminhoDeMudanca;
  rotulo: string;
  icone: LucideIcon;
  /** A pergunta que a aba responde, em uma linha, para o cabeçalho. */
  resumo: string;
}

export const ABAS: readonly AbaDeMudanca[] = [
  {
    caminho: CAMINHOS.calendario,
    rotulo: 'Calendário',
    icone: CalendarRange,
    resumo:
      'O que sobe, quando, e o que colide. É esta a tela que o Comitê de Mudanças (CAB) abre na reunião: janelas, congelamentos e a Interrupção Projetada de Serviço no mesmo período.',
  },
  {
    caminho: CAMINHOS.janelas,
    rotulo: 'Janelas',
    icone: Timer,
    resumo:
      'Os períodos em que a manutenção pode acontecer. Mover uma janela move todas as mudanças dentro dela — e o servidor recusa o movimento que jogaria alguma para dentro de um congelamento.',
  },
  {
    caminho: CAMINHOS.congelamentos,
    rotulo: 'Congelamentos',
    icone: Snowflake,
    resumo:
      'Os períodos em que não se mexe no ambiente. Bloqueante recusa o agendamento e só cede a uma exceção aprovada; informativo apenas avisa.',
  },
] as const;

export const abaDe = (caminho: string): AbaDeMudanca | undefined =>
  ABAS.find((aba) => aba.caminho === caminho);

interface PropsDeLink {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  className?: string;
  title?: string;
  children: ReactNode;
}

const LinkDeRota = Link as unknown as ComponentType<PropsDeLink>;

export function LinkDoCalendario({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota to={`${RAIZ_DE_MUDANCAS}/${CAMINHOS.calendario}`} className={className}>
      {children}
    </LinkDeRota>
  );
}

/**
 * Link para a ficha da mudança.
 *
 * O destino é o **chamado**, na aba de mudança: a mudança é extensão 1:1 do
 * chamado (ADR-001), e dar a ela um endereço próprio criaria duas páginas para
 * o mesmo registro — uma com comentários e anexos, outra com planos e risco.
 */
export function LinkDaMudanca({
  ticketId,
  className,
  title,
  children,
}: {
  ticketId: string;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <LinkDeRota
      to="/chamados/$ticketId"
      params={{ ticketId }}
      search={{ aba: 'mudanca' }}
      className={className}
      title={title}
    >
      {children}
    </LinkDeRota>
  );
}
